import { computeTablePricing, type BillingFrequency, type CurrencyConfig, type FrequencyTotals } from '@proposify/pricing-engine';
import type { TableColumnType, TableRowType } from '../../../../grid/reduxStore/table.types';
import { ColumnTypeIdentifier } from '../../../../grid/reduxStore/table.types';
import type { CurrencySettings } from '../../../../../../services/documents/DocumentRepository';
import { DEFAULT_CURRENCY_CODE } from '../../../../grid/reduxStore/documentSettingsSlice';
import { toEngineRows, toEngineColumns, hasPricingInput } from './tableEngineMapper';

export type EngineRowsWithCalculatedSubtotals = {
  textRowsWithCalculatedSubtotalsColumn: TableRowType[];
  sumOfSubtotalColumn: string;
  presentFrequencies: BillingFrequency[];
  frequencyTotals: FrequencyTotals;
};

// Engine-backed replacement for getTextRowsWithCalculatedSubtotals on preview
// surfaces. Values stay decimal strings end-to-end; rows with a computed
// subtotal are enriched with a `_pricingCalculation` payload for the discount
// cell (parity with the editor's selectRows).
export function getEngineRowsWithCalculatedSubtotals(
  rows: TableRowType[],
  columns: TableColumnType[],
  currencySettings: CurrencySettings | undefined,
  pricingDecimalPlaces: number
): EngineRowsWithCalculatedSubtotals {
  const currencyConfig: CurrencyConfig = {
    code: currencySettings?.currencyCode || DEFAULT_CURRENCY_CODE,
    symbol: currencySettings?.currencySign || '$',
    symbolPosition: 'front',
    decimalSeparator: '.',
    thousandsSeparator: ',',
    decimalPlaces: 2,
    roundingIncrement: '0.01',
  };
  const engineRows = toEngineRows(rows, columns);
  const engineColumns = toEngineColumns(columns);
  const result = computeTablePricing({
    rows: engineRows,
    columns: engineColumns,
    currency: currencyConfig,
    settings: { precision: pricingDecimalPlaces, roundingMode: 'nearest', currency: currencyConfig },
  });
  const subtotalColumn = columns.find((col) => col.columnType === ColumnTypeIdentifier.SUBTOTAL);
  // toEngineRows is a 1:1 map over `rows`, so engineRows[index] is rows[index]'s
  // own engine counterpart — no id lookup needed (and no duplicate-id risk).
  const rowsWithSubtotals = rows.map((row, index) => {
    const derived = result.rows[row.id];
    if (!subtotalColumn || !derived) return row;
    const engineRow = engineRows[index];
    // Gate on pricing input, not computed output — see hasPricingInput.
    if (!hasPricingInput(engineRow, engineColumns)) return row;
    return {
      ...row,
      [subtotalColumn.field]: derived.discountedSubtotal,
      _pricingCalculation: {
        originalSubtotal: derived.subtotal,
        discountAmount: derived.discountAmount,
        discountedSubtotal: derived.discountedSubtotal,
        formattedSubtotal: derived.formattedSubtotal,
        includedInTotal: derived.includedInTotal,
        billingFrequency: derived.billingFrequency,
      },
    };
  });

  return {
    textRowsWithCalculatedSubtotalsColumn: rowsWithSubtotals,
    sumOfSubtotalColumn: result.grandTotal,
    presentFrequencies: result.presentFrequencies,
    frequencyTotals: result.frequencyTotals,
  };
}
